import { useEffect, useState, Fragment, useCallback } from 'react';
import axios from 'axios';
import ModalDeleteFile from './ModalDeleteFile'
import ModalVideoManager from './ModalVideomanager';
import ModalConvertVideo from './ModalConvertVideo';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import {
    FolderIcon,
    FilmIcon,
    TrashIcon,
    HomeIcon,
    ChevronRightIcon,
    ArrowUpTrayIcon,
    MagnifyingGlassIcon,
    BoltIcon,
} from '@heroicons/react/24/outline';

// Médiathèque — navigation par dossier avec fil d'Ariane (l'ancienne version ne permettait
// PAS de remonter d'un dossier), tri, recherche, suppression sûre (stopPropagation : le clic
// sur la corbeille n'ouvre plus la prévisualisation), jauge de stockage.
//
// Outil « récupérer de l'espace » : on croise la liste des fichiers avec /mediaUsage (références
// programmes/devices + dernière lecture issue du flux now_playing) pour marquer chaque fichier
// UTILISÉ / NON UTILISÉ, afficher sa dernière lecture, filtrer les inutilisés et chiffrer l'espace
// récupérable -> suppression SÉLECTIVE (corbeille par ligne), sans tout purger d'un coup.

const API = () => `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}`;

function fmtDate(value) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

// "YYYY/MM/DD HH:mm:ss" (renvoyé par le back) ou null -> Date, ou null si absent/illisible.
function parseLastPlayed(value) {
    if (!value) return null;
    const t = new Date(String(value).replace(/\//g, '-').replace(' ', 'T')).getTime();
    return Number.isNaN(t) ? null : t;
}

// Octets -> chaîne lisible (pour l'espace récupérable ; le back envoie déjà fileSize par fichier).
function fmtBytes(bytes) {
    if (!bytes || bytes <= 0) return '0 o';
    const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

// Repli si fileSizeBytes est absent (réponses back antérieures) : parse "646.1 MB" -> octets.
function sizeToBytes(entry) {
    if (typeof entry.fileSizeBytes === 'number') return entry.fileSizeBytes;
    const m = String(entry.fileSize || '').trim().match(/([\d.]+)\s*([KMGT]?)B?/i);
    if (!m) return 0;
    const n = parseFloat(m[1]) || 0;
    const mult = { '': 1, K: 1024, M: 1024 ** 2, G: 1024 ** 3, T: 1024 ** 4 }[m[2].toUpperCase()] || 1;
    return n * mult;
}

function FileManager() {
    const [datas, setDate] = useState([])
    const [storage, setStorage] = useState({ free: '', total: '' })
    const [showModal, setShowModal] = useState(false);
    const [nameFile, setNameFile] = useState();
    // Fichier ciblé par la conversion (null = modal fermé). { fileName, dirName }
    const [convertFile, setConvertFile] = useState(null);
    const [showVideo, setShowVideo] = useState(false);
    const [search, setSearch] = useState('');
    const [sortedBy, setSortedBy] = useState('fileDate');
    const [counter, setCounter] = useState(0)
    const [loading, setLoading] = useState(true);
    // Usage parc-wide : { usage: {basename: refCount}, lastPlayed: {basename: "YYYY/MM/DD HH:mm:ss"} }
    const [usage, setUsage] = useState({});
    const [lastPlayed, setLastPlayed] = useState({});
    const [onlyUnused, setOnlyUnused] = useState(false);
    // Chemin courant en SEGMENTS (ex. ['clients', 'alta']) — source de vérité de la navigation.
    const [path, setPath] = useState([]);
    const [choosenVideo, setChoosenVideo] = useState({
        fileName: '',
        fileDate: '',
        fileSize: '',
        isDir: '',
        firstDirName: ''
    });

    // L'API attend les sous-dossiers joints par '{-}' (cf. listVideos/:dirName côté back).
    const loadPath = (segments) => {
        setLoading(true);
        const joined = segments.join('{-}');
        const url = joined ? `${API()}/listVideos/${encodeURIComponent(joined)}` : `${API()}/listVideos`;
        axios.get(url)
            .then(res => {
                setDate(res.data.files || []);
                if (res.data.diskSpace) setStorage(res.data.diskSpace);
            })
            .catch(err => { console.log(err); toast.error('Impossible de lister ce dossier.'); })
            .finally(() => setLoading(false));
    };

    // Usage des médias (références + dernière lecture) — parc-wide, indépendant du dossier courant.
    const loadUsage = useCallback(() => {
        axios.get(`${API()}/mediaUsage`)
            .then(res => { setUsage(res.data?.usage || {}); setLastPlayed(res.data?.lastPlayed || {}); })
            .catch(() => { /* non bloquant : la médiathèque reste utilisable sans le marquage */ });
    }, []);

    useEffect(() => { loadPath([]); loadUsage(); /* racine */ }, [loadUsage]); // eslint-disable-line react-hooks/exhaustive-deps

    const enterDir = (dirName) => { const p = [...path, dirName]; setPath(p); setSearch(''); loadPath(p); };
    const goToDepth = (depth) => { const p = path.slice(0, depth); setPath(p); setSearch(''); loadPath(p); };

    // % utilisé, calculé à l'affichage (l'ancien code lisait un state pas encore mis à jour -> NaN).
    const freeNum = parseFloat(storage.free);
    const totalNum = parseFloat(storage.total);
    const usedPct = Number.isFinite(freeNum) && Number.isFinite(totalNum) && totalNum > 0
        ? Math.min(100, Math.max(0, Math.round((1 - freeNum / totalNum) * 100)))
        : 0;

    // Enrichit chaque fichier avec son statut d'usage + sa dernière lecture (par nom de fichier).
    const refCount = (entry) => entry.isDir ? null : (usage[entry.fileName] || 0);
    const isUnused = (entry) => !entry.isDir && refCount(entry) === 0;
    const lastPlayedMs = (entry) => entry.isDir ? null : parseLastPlayed(lastPlayed[entry.fileName]);

    const sorted = [...datas].sort((a, b) => {
        // Dossiers toujours en tête (comme un explorateur).
        if (!!a.isDir !== !!b.isDir) return a.isDir ? -1 : 1;
        const asc = counter % 2 === 0;
        let cmp;
        if (sortedBy === 'fileDate') {
            cmp = new Date(a.fileDate) - new Date(b.fileDate);
        } else if (sortedBy === 'fileSize') {
            // Tri NUMÉRIQUE en octets : "1006 KB" ne doit plus passer devant "646 MB" (bug corrigé).
            cmp = sizeToBytes(a) - sizeToBytes(b);
        } else if (sortedBy === 'lastPlayed') {
            cmp = (lastPlayedMs(a) ?? -Infinity) - (lastPlayedMs(b) ?? -Infinity);
        } else if (sortedBy === 'used') {
            cmp = (refCount(a) ?? 0) - (refCount(b) ?? 0);
        } else {
            cmp = String(a[sortedBy] ?? '').localeCompare(String(b[sortedBy] ?? ''), 'fr', { numeric: true });
        }
        return asc ? cmp : -cmp;
    });

    const visible = sorted
        .filter((item) => search.toLowerCase() === '' ? true : item.fileName.toLowerCase().includes(search.toLowerCase()))
        // Filtre "inutilisés seulement" : on garde les dossiers (navigation) + les fichiers non utilisés.
        .filter((item) => !onlyUnused ? true : (item.isDir || isUnused(item)));

    const dirCount = visible.filter(f => f.isDir).length;
    const fileCount = visible.length - dirCount;

    // Espace récupérable = somme des tailles des fichiers NON UTILISÉS du dossier courant.
    const unusedFiles = datas.filter((f) => isUnused(f));
    const reclaimableBytes = unusedFiles.reduce((sum, f) => sum + sizeToBytes(f), 0);

    const handleDeleteUselessVideos = () => {
        if (!window.confirm('Supprimer TOUS les fichiers non utilisés de tout le parc (référencés par aucun programme ni device) ? Action irréversible.')) return;
        axios.delete(`${API()}/deleteUselessVideo`)
            .then(res => {
                if (res.data && res.data.length > 0) {
                    res.data.forEach((file) => toast.success(`Vidéo supprimée : ${file}`));
                    setTimeout(() => window.location.reload(false), Math.min(res.data.length, 5) * 1000);
                } else {
                    toast.info('Aucune vidéo à supprimer.');
                    setTimeout(() => window.location.reload(false), 2000);
                }
            }).catch(err => console.log(err))
    }

    const sortHeader = (key, label, extraClass = '') => (
        <th
            scope="col"
            className={`cursor-pointer whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700 ${extraClass}`}
            onClick={() => { setSortedBy(key); setCounter(counter + 1) }}
        >
            <span className="inline-flex items-center gap-1">
                {label}
                {sortedBy === key && <span className="text-indigo-500">{counter % 2 === 0 ? '▲' : '▼'}</span>}
            </span>
        </th>
    );

    return (
        <Fragment>
            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
                {/* En-tête + actions */}
                <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h1 className="text-xl font-semibold text-slate-800">Médiathèque</h1>
                        <p className="text-sm text-slate-500">
                            {dirCount} dossier{dirCount > 1 ? 's' : ''} · {fileCount} fichier{fileCount > 1 ? 's' : ''}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
                            onClick={handleDeleteUselessVideos}
                            title="Supprime d'un coup TOUS les fichiers non utilisés du parc"
                        >
                            <TrashIcon className="h-4 w-4" />
                            Tout purger
                        </button>
                        <Link to="/uploadfile">
                            <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500">
                                <ArrowUpTrayIcon className="h-4 w-4" />
                                Uploader
                            </button>
                        </Link>
                    </div>
                </div>

                {/* Stockage + espace récupérable */}
                <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-2 flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-700">Stockage serveur</span>
                        <span className="text-slate-500">
                            <span className="font-semibold text-slate-700">{storage.free || '—'}</span> libre sur {storage.total || '—'}
                        </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                        <div
                            className={`h-full rounded-full transition-all duration-500 ${usedPct > 90 ? 'bg-red-500' : usedPct > 75 ? 'bg-amber-500' : 'bg-indigo-500'}`}
                            style={{ width: `${usedPct}%` }}
                        />
                    </div>
                    <div className="mt-1 flex items-center justify-between text-xs">
                        <span className="text-slate-500">
                            {unusedFiles.length > 0
                                ? <>💾 <span className="font-semibold text-emerald-600">{fmtBytes(reclaimableBytes)}</span> récupérables dans ce dossier ({unusedFiles.length} fichier{unusedFiles.length > 1 ? 's' : ''} non utilisé{unusedFiles.length > 1 ? 's' : ''})</>
                                : <span className="text-slate-400">Aucun fichier non utilisé dans ce dossier</span>}
                        </span>
                        <span className="text-slate-400">{usedPct}% utilisé</span>
                    </div>
                </div>

                {/* Fil d'Ariane + recherche + filtre */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <nav className="flex items-center gap-1 text-sm">
                        <button
                            type="button"
                            onClick={() => goToDepth(0)}
                            className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 transition ${path.length === 0 ? 'font-semibold text-slate-800' : 'text-indigo-600 hover:bg-indigo-50'}`}
                        >
                            <HomeIcon className="h-4 w-4" /> Médias
                        </button>
                        {path.map((seg, i) => (
                            <Fragment key={`${seg}-${i}`}>
                                <ChevronRightIcon className="h-4 w-4 text-slate-300" />
                                <button
                                    type="button"
                                    onClick={() => goToDepth(i + 1)}
                                    className={`rounded-lg px-2 py-1 transition ${i === path.length - 1 ? 'font-semibold text-slate-800' : 'text-indigo-600 hover:bg-indigo-50'}`}
                                >
                                    {seg}
                                </button>
                            </Fragment>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setOnlyUnused((v) => !v)}
                            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${onlyUnused ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                            title="N'afficher que les fichiers référencés par aucun programme ni device"
                        >
                            <span className={`h-2 w-2 rounded-full ${onlyUnused ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                            Inutilisés seulement
                        </button>
                        <div className="relative flex h-10 w-full items-center rounded-xl border border-slate-200 bg-white shadow-sm focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20 sm:w-72">
                            <MagnifyingGlassIcon className="ml-3 h-5 w-5 text-slate-400" />
                            <input
                                className="h-full w-full rounded-xl bg-transparent px-3 text-sm text-slate-800 placeholder-slate-400 outline-none"
                                type="text"
                                placeholder="Rechercher dans ce dossier…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)} />
                        </div>
                    </div>
                </div>

                {/* Table des fichiers */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-slate-700">
                            <thead className="border-b border-slate-200 bg-slate-50">
                                <tr>
                                    {sortHeader('fileName', 'Nom')}
                                    {sortHeader('used', 'Statut')}
                                    {sortHeader('lastPlayed', 'Dernière lecture')}
                                    {sortHeader('fileDate', 'Ajouté le')}
                                    {sortHeader('fileSize', 'Taille')}
                                    <th scope="col" className="w-16 px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400">Chargement…</td></tr>
                                ) : visible.length === 0 ? (
                                    <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400">
                                        {onlyUnused ? 'Aucun fichier non utilisé dans ce dossier.' : search ? 'Aucun résultat pour cette recherche.' : 'Dossier vide.'}
                                    </td></tr>
                                ) : visible.map((data) => data.isDir ? (
                                    <tr
                                        key={`d-${data.fileName}`}
                                        className="cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-indigo-50/40"
                                        onClick={() => enterDir(data.fileName)}
                                    >
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center gap-2.5 font-semibold text-slate-800">
                                                <FolderIcon className="h-5 w-5 shrink-0 text-amber-500" />
                                                {data.fileName}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-300">—</td>
                                        <td className="px-4 py-3 text-slate-300">—</td>
                                        <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(data.fileDate)}</td>
                                        <td className="whitespace-nowrap px-4 py-3 text-slate-500">—</td>
                                        <td className="px-4 py-3 text-right text-slate-300">
                                            <ChevronRightIcon className="ml-auto h-4 w-4" />
                                        </td>
                                    </tr>
                                ) : (
                                    <tr
                                        key={`f-${data.fileName}`}
                                        className={`cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-indigo-50/40 ${isUnused(data) ? 'bg-emerald-50/30' : ''}`}
                                        onClick={() => {
                                            setShowVideo(true);
                                            setChoosenVideo({ ...choosenVideo, fileName: data.fileName, fileDate: data.fileDate, fileSize: data.fileSize, firstDirName: path.join('{-}') });
                                        }}
                                    >
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center gap-2.5 font-medium text-slate-800">
                                                <FilmIcon className="h-5 w-5 shrink-0 text-indigo-400" />
                                                {data.fileName}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3">
                                            {isUnused(data) ? (
                                                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
                                                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Non utilisé
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"
                                                    title={`Référencé par ${refCount(data)} programme(s)/device(s)`}>
                                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Utilisé ({refCount(data)})
                                                </span>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                                            {lastPlayedMs(data) ? fmtDate(lastPlayedMs(data)) : <span className="text-slate-300">Jamais</span>}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDate(data.fileDate)}</td>
                                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs tabular-nums text-slate-500">{data.fileSize}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    title="Convertir (variante plateforme)"
                                                    className="rounded-lg p-1.5 text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-600"
                                                    onClick={(e) => {
                                                        // stopPropagation : sinon le clic ouvre AUSSI la prévisualisation vidéo.
                                                        e.stopPropagation();
                                                        setConvertFile({ fileName: data.fileName, dirName: path.join('{-}') });
                                                    }}
                                                >
                                                    <BoltIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    value={data.fileName}
                                                    title="Supprimer"
                                                    className="rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                                                    onClick={(e) => {
                                                        // stopPropagation : sinon le clic ouvre AUSSI la prévisualisation vidéo.
                                                        e.stopPropagation();
                                                        setShowModal(true);
                                                        setNameFile(path.length === 0 ? data.fileName : path.join('/') + '/' + data.fileName);
                                                    }}
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <ToastContainer />
            </div>
            <ModalVideoManager isVisible={showVideo} onClose={() => setShowVideo(false)} videoDati={choosenVideo} />
            <ModalDeleteFile
                isVisible={showModal}
                onClose={() => setShowModal(false)}
                nameFile={nameFile}
                onDeleted={(deletedNameFile) => {
                    // Rafraîchissement EN PLACE : on retire juste la ligne de l'état (pas de reload
                    // -> scroll, filtre "inutilisés", recherche et tri sont conservés). `nameFile` peut
                    // être préfixé du dossier (sous-dossier/fichier) ; on compare sur le nom de base.
                    const base = String(deletedNameFile).split('/').pop();
                    setDate((rows) => rows.filter((f) => f.fileName !== base));
                    toast.success(`Fichier supprimé : ${base}`);
                    loadUsage(); // met à jour statut/espace récupérable en fond, sans recharger la liste
                }}
            />
            <ModalConvertVideo
                isVisible={!!convertFile}
                fileName={convertFile?.fileName}
                dirName={convertFile?.dirName}
                onClose={() => setConvertFile(null)}
                onDone={() => { loadPath(path); loadUsage(); }}
            />
        </Fragment>
    )
}
export default FileManager;
